'use client'

import { useCallback, useMemo } from 'react'
import {
  CduiAdapterProvider,
  type CduiImageProps,
  type CduiLinkProps,
} from '@shipt/cdui-modules/component-adapters'
import { ApplicationContextProvider } from '@shipt/cdui-modules/context'
import { ActionProvider } from '@shipt/cdui-modules/action-provider'
import { CDUIAnalyticsProvider, RegistryProvider } from '@shipt/cdui-modules/analytics'
import { NextLink } from '@/features/shared/elements/Link'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useCduiActions } from '@/features/cdui/actions'
import { usePromotionNavigation } from '@/features/cdui/promotionNavigation'
import { trackCduiAnalytics } from '@/features/cdui/analytics'
import { cduiModuleRegistry } from '@/features/cdui/registry'
import { useBasket } from '@/features/shared/hooks/useBasket'
import { logWarn } from '@/features/shared/utils/logger'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import {
  desktopSpacingToken,
  type PageSpacingSettings,
  type SpacingToken,
} from '@shipt/cdui-core/pages/settings'

const CDUI_THEME = 'refresh_light_marketplace'

// CDUI's ApplicationContext still types these, but web modules only read
// getProductCountById. AddButton emits cart changes through ActionProvider.
const unusedCartMutation = () => undefined

// `none` is a literal 0; every other token resolves through the astro theme's
// --spacing_* custom property. Mirrors spacingCssValue in cdui/apps/preview.
function spacingCssValue(token: SpacingToken): string {
  return token === 'none' ? '0' : `var(--spacing_${token})`
}

/**
 * Author-controlled page rhythm as inline custom properties consumed by
 * .cdui-page-root. Custom properties keep more than one slot on the page from
 * fighting over a single injected <style> block.
 */
function spacingVars(
  spacing: PageSpacingSettings,
): React.CSSProperties & Record<`--${string}`, string> {
  return {
    '--cdui-space-between': spacingCssValue(spacing.paddingBetween.mobile),
    '--cdui-space-before-first': spacingCssValue(spacing.paddingBeforeFirst.mobile),
    '--cdui-space-after-last': spacingCssValue(spacing.paddingAfterLast.mobile),
    '--cdui-space-between-desktop': spacingCssValue(desktopSpacingToken(spacing.paddingBetween)),
    '--cdui-space-before-first-desktop': spacingCssValue(
      desktopSpacingToken(spacing.paddingBeforeFirst),
    ),
    '--cdui-space-after-last-desktop': spacingCssValue(
      desktopSpacingToken(spacing.paddingAfterLast),
    ),
  }
}

const SEGWAY_ORIGIN = 'https://www.shipt.com'

/**
 * The only protocols a CMS-authored href may carry into the DOM. Same shape as
 * the isSafeSegwayURL guard actions.ts puts in front of banner deep links —
 * resolve against Segway's origin inside a try/catch, then check an allowlist —
 * so both CMS link paths stay one mechanism. Parsing is the part that matters:
 * the URL parser strips the padding that walks "java\nscript:alert(1)" past a
 * startsWith() blocklist. mailto:/tel: are rejected too; widen this set rather
 * than loosening the check if authors ever need them.
 */
const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:'])

/** The resolved href, or null when it is unparseable or on a rejected protocol. */
const safeLinkUrl = (href: string): URL | null => {
  // An empty href resolves to the current page, which no module ever means.
  if (href === '') return null
  try {
    const url = new URL(href, SEGWAY_ORIGIN)
    return SAFE_LINK_PROTOCOLS.has(url.protocol) ? url : null
  } catch {
    return null
  }
}

/**
 * Same-origin app paths go through Segway's NextLink (NProgress, PCI hard
 * navigation, same-URL no-op). The resolved origin decides, not the leading
 * slash: `//host` and `/\host` both resolve off-origin, so neither may reach
 * the router.
 */
const isClientRoutableHref = (href: string, url: URL): boolean =>
  href.startsWith('/') && url.origin === SEGWAY_ORIGIN

export const CduiNextLink = ({ href, target, rel, children, ...rest }: CduiLinkProps) => {
  const authored = typeof href === 'string' ? href : ''
  const url = safeLinkUrl(authored)

  // A rejected href renders its children inside an anchor with no href — the
  // HTML placeholder link. Modules use CduiLink as their layout wrapper (the
  // product card and the store tiles pass className), so dropping the element
  // would collapse the grid around it, while dropping only the href keeps the
  // box and takes away the navigation. Nothing on this path can throw, so a
  // hostile href degrades the link instead of failing the RSC render.
  if (!url) {
    if (authored !== '') logWarn('[CDUI] Blocked unsafe link href', { href: authored })
    return <a {...rest}>{children}</a>
  }

  // A new tab hands the opened document a window.opener handle back to this
  // one, so rel is the adapter's call rather than the author's.
  const safeRel = target === '_blank' ? 'noopener noreferrer' : rel

  if (isClientRoutableHref(authored, url)) {
    return (
      <NextLink {...rest} href={authored} target={target} rel={safeRel}>
        {children}
      </NextLink>
    )
  }

  return (
    <a {...rest} href={authored} target={target} rel={safeRel}>
      {children}
    </a>
  )
}

/**
 * Product photos are served by the product imager, a Fastly IO host that
 * resizes on request. CDUI's product cards and coupons draw them as thumbnails:
 * 144px in a portrait card (w-40 less the photo box's sm padding), 88px in a
 * landscape card or a coupon.
 */
const PRODUCT_PHOTO_HOSTS = new Set(['dsom-imager-prod.shipt.com', 'dsom-imager.staging.shipt.com'])
const PRODUCT_PHOTO_PX = 144

const isProductPhoto = (src: unknown): src is string => {
  if (typeof src !== 'string') return false
  try {
    return PRODUCT_PHOTO_HOSTS.has(new URL(src).hostname)
  } catch {
    return false
  }
}

/**
 * CDUI passes no image dimensions, and next/image needs one to choose widths,
 * so only product photos, whose size is known, go through Segway's Image: the
 * Fastly loader and broken-image fallback legacy product cards use, here
 * asking for width=256 at 1x and 384 at 2x. CDUI's eager/lazy choice passes
 * through. Any other image keeps CDUI's own lazy <img>: CMS banners and store
 * logos are on Fastly hosts too, but at sizes this adapter can't know, and a
 * guessed width would blur a banner.
 */
export const CduiNextImage = ({ src, alt, loading, decoding, ...rest }: CduiImageProps) => {
  const altText = typeof alt === 'string' ? alt : ''

  if (!isProductPhoto(src)) {
    return (
      // oxlint-disable-next-line @next/next/no-img-element -- CDUI's own default image, see above
      <img
        {...rest}
        src={src}
        alt={altText}
        decoding={decoding ?? 'async'}
        loading={loading ?? 'lazy'}
      />
    )
  }

  return (
    <Image
      {...rest}
      src={src}
      alt={altText}
      loading={loading}
      width={PRODUCT_PHOTO_PX}
      height={PRODUCT_PHOTO_PX}
    />
  )
}

function useCduiCart() {
  const { getProductQuantity } = useBasket()
  const getProductCountById = useCallback(
    (productId: string) => {
      const id = Number(productId)
      return Number.isSafeInteger(id) && id > 0 ? getProductQuantity({ id }) : 0
    },
    [getProductQuantity],
  )

  return useMemo(
    () => ({
      getProductCountById,
      onMutate: unusedCartMutation,
      onRemove: unusedCartMutation,
    }),
    [getProductCountById],
  )
}

/**
 * Client-side host bridge for the app-router RSC slot.
 *
 * Provides:
 *  - CduiAdapterProvider        — swaps CDUI's default <a> for Segway's NextLink,
 *                                 and product photos' <img> for Segway's Image
 *  - ApplicationContextProvider — live cart counts (mutations go through onAction)
 *                                 and promotion navigation for product cards
 *  - ActionProvider             — dispatches module actions to Segway handlers
 *  - data-theme root            — activates @shipt/astro v2 design tokens
 */
export function CduiSlotProviders({
  children,
  spacing,
}: {
  children: React.ReactNode
  spacing: PageSpacingSettings
}) {
  const onAction = useCduiActions()
  const cart = useCduiCart()
  const navigation = usePromotionNavigation()
  const location = urlPathnameToLocationName()

  return (
    <div className="cdui-page-root" data-theme={CDUI_THEME} style={spacingVars(spacing)}>
      <CDUIAnalyticsProvider track={trackCduiAnalytics} location={location}>
        <RegistryProvider value={cduiModuleRegistry}>
          <CduiAdapterProvider adapters={{ Image: CduiNextImage, Link: CduiNextLink }}>
            <ApplicationContextProvider cart={cart} navigation={navigation}>
              <ActionProvider onAction={onAction}>{children}</ActionProvider>
            </ApplicationContextProvider>
          </CduiAdapterProvider>
        </RegistryProvider>
      </CDUIAnalyticsProvider>
    </div>
  )
}
